// src/oura/visibility/questionSets.ts
//
// The question sets a business has been measured on, as versions (v1, v2, …),
// and what changed between two of them. The Question history drawer, the
// version tabs on Named over time, the change banner and the New chip all read
// from here, so the page says one thing about which questions are which.
//
// A version is one `questionSetId`, a hash of the questions' text: two scans
// share it only when every question is identical, so the scans of one version
// compare like for like and the line may join them. Versions are numbered in
// the order each set was first asked. A version changes ONLY when the questions
// change (#4583, Matthew, 22 Oct): a trend break for any other cause (a re-ask
// copy, ChatGPT asked from another location, the instrument or the counting
// rule) never starts one, and a set asked again after another set is the same
// version (Lush: A, A, C, A is V1, V1, V2, V1). A scan with no set id on record
// (from before the history carried one) belongs to no version, and nothing is
// guessed for it.
//
// The diff joins questions on their text, the key the backend joins on too. A
// question whose slot `n` and fill (the area or service it was built from,
// none for a slot that takes no fill) match one that left, but whose words
// differ, is REWORDED: shown beside its old words. It still counts as swapped,
// never as kept: different words get different answers, so the like-for-like
// count leaves it out. A question with no slot number is never paired.

import type { AeoQuery, AeoScoreHistoryEntry } from "@service/aeo";
import { sameLocalDay } from "./format";
import type { VisTrend } from "./model";

/** Below this many questions in both sets, a change between them is not reported (the line's own rule). */
export const MIN_SHARED = 25;

export interface QuestionSetScan {
    assessmentId: string;
    capturedAt: string;
    named: number | null;
    of: number | null;
    /** When the client could first see it; null while held. Undefined when the history does not say. */
    publishedAt?: string | null;
}

export interface QuestionSetVersion {
    /** 1-based, in the order the set was first asked. */
    n: number;
    setId: string;
    /** Oldest first. */
    scans: QuestionSetScan[];
    /** Why the set started, in the backend's own words, when its first scan recorded one. */
    reason: string | null;
    /** The same, as codes a client can be shown (ourteam-backend#5914). Empty before codes existed. */
    codes: string[];
}

/** "v3". */
export const versionName = (n: number) => `V${n}`;

const scored = (h: AeoScoreHistoryEntry) =>
    (h.status ?? "complete") === "complete" && !h.gated && !!h.visibility?.questionSetId;

/** Every question set the history records, oldest first. Empty when no scan carries a set id. */
export function questionSetVersions(history: AeoScoreHistoryEntry[]): QuestionSetVersion[] {
    const sorted = history.filter(scored).sort((a, b) => Date.parse(a.captured_at) - Date.parse(b.captured_at));
    const byId = new Map<string, QuestionSetVersion>();
    const out: QuestionSetVersion[] = [];
    // One version per question set, whatever came between (#4583). A trend
    // break still explains how a set started (`reason`), and the chart still
    // keeps scans measured another way unjoined (trendBreak.ts), but it no
    // longer decides the version.
    sorted.forEach((h) => {
        const v = h.visibility;
        const id = v?.questionSetId;
        if (!v || !id) return;
        let version = byId.get(id);
        if (!version) {
            version = { n: out.length + 1, setId: id, scans: [], reason: v.trendBreak?.reason ?? null, codes: v.trendBreak?.codes ?? [] };
            byId.set(id, version);
            out.push(version);
        }
        version.scans.push({
            assessmentId: h.assessment_id,
            capturedAt: h.captured_at,
            named: v.questionsNamed,
            of: v.questionsOf,
            publishedAt: h.published_at,
        });
    });
    return out;
}

/**
 * Whether the client sees a version: `yes` when every scan of it is published,
 * `no` when none is, `partly` in between. Null when the history does not say
 * (an older backend), where nothing is held. A version is not a record of its
 * own: it is shown to the client exactly when its scans are, so its switch
 * publishes or hides those scans, the same gate "Publish to client" uses.
 */
export type VersionPublished = "yes" | "no" | "partly";

export function versionPublished(version: QuestionSetVersion): VersionPublished | null {
    const known = version.scans.filter((s) => s.publishedAt !== undefined);
    if (known.length === 0) return null;
    const on = known.filter((s) => !!s.publishedAt).length;
    return on === known.length ? "yes" : on === 0 ? "no" : "partly";
}

/**
 * Each scan's version, by its question set: the staff page's numbers, which the
 * merchant's chart and scan picker now show too (#4657). A client's first set
 * is V1, and a scan with no set on record has no number: nothing is guessed.
 */
export function versionNumbers(history: AeoScoreHistoryEntry[]): Map<string, number> {
    const out = new Map<string, number>();
    questionSetVersions(history).forEach((v) => v.scans.forEach((s) => out.set(s.assessmentId, v.n)));
    return out;
}

/**
 * The scans the client can see: a held scan (`published_at` null) is left out.
 * The client's own history never carries one; staff on "View as the merchant"
 * read the full history, so the client's numbers are counted from this (Ownlab:
 * its one published scan is V1, not the V4 the staff page numbers it).
 */
export const clientScans = (history: AeoScoreHistoryEntry[]) => history.filter((h) => h.published_at !== null);

/**
 * The merchant's line, broken only where the questions changed (#4657,
 * Matthew): two neighbouring points on different question sets. Where, how or
 * by which model a scan was asked never breaks it; the backend keeps those as
 * `measureNotes` (see `measureNoteDates`). Points with no set on record join.
 * The answers series (no sets) is returned as it is.
 */
export function questionsOnlyTrend(trend: VisTrend, history: AeoScoreHistoryEntry[]): VisTrend {
    if (trend.unit !== "questions") return trend;
    const setOn = new Map(history.map((h) => [h.captured_at, h.visibility?.questionSetId ?? null]));
    let breakIndex: number | null = null;
    for (let i = trend.points.length - 1; i > 0; i -= 1) {
        const now = setOn.get(trend.points[i].date ?? "") ?? null;
        const before = setOn.get(trend.points[i - 1].date ?? "") ?? null;
        if (now && before && now !== before) {
            breakIndex = i;
            break;
        }
    }
    return breakIndex === null
        ? { ...trend, breakIndex: null, breakReason: null, breakCause: null }
        : { ...trend, breakIndex, breakReason: "the question set changed here", breakCause: "questions" };
}

/** The dates on the line measured differently with the same questions (`measureNotes`): said in a note, never a break. */
export function measureNoteDates(trend: VisTrend, history: AeoScoreHistoryEntry[]): string[] {
    const noted = new Map(history.map((h) => [h.captured_at, (h.visibility?.measureNotes ?? []).length > 0]));
    return trend.points
        .slice(trend.breakIndex ?? 0)
        .flatMap((p) => (p.date && noted.get(p.date) ? [p.date] : []));
}

/** The version a scan was measured on, or null when the history does not say. */
export function versionOfScan(versions: QuestionSetVersion[], assessmentId: string | null | undefined): QuestionSetVersion | null {
    if (!assessmentId) return null;
    return versions.find((v) => v.scans.some((s) => s.assessmentId === assessmentId)) ?? null;
}

/**
 * The last scored scan before a version's first scan, on another set: what that
 * version's questions are read against ("New in vN", the change banner). Not
 * the earlier version's last scan: a set asked again later (#4583) has scans
 * after this version started. Null for a version with nothing before it.
 */
export function scanBeforeVersion(versions: QuestionSetVersion[], version: QuestionSetVersion | null): QuestionSetScan | null {
    if (!version) return null;
    const start = Date.parse(version.scans[0].capturedAt);
    const before = versions
        .filter((v) => v.n !== version.n)
        .flatMap((v) => v.scans)
        .filter((s) => Date.parse(s.capturedAt) < start)
        .sort((a, b) => Date.parse(b.capturedAt) - Date.parse(a.capturedAt));
    return before[0] ?? null;
}

/**
 * The set change the scan on screen starts, when it is the first scan on a set
 * the scan before it did not ask: the moment the banner and the New chip are
 * for. Null on every later scan of that set, and when either scan has no set id.
 */
export function changeAtScan(
    versions: QuestionSetVersion[],
    history: AeoScoreHistoryEntry[],
    assessmentId: string | null | undefined,
): { from: QuestionSetVersion; to: QuestionSetVersion; previousScanId: string } | null {
    const to = versionOfScan(versions, assessmentId);
    if (!to || to.scans[0].assessmentId !== assessmentId) return null;
    const earlier = history
        .filter(scored)
        .filter((h) => Date.parse(h.captured_at) < Date.parse(to.scans[0].capturedAt))
        .sort((a, b) => Date.parse(b.captured_at) - Date.parse(a.captured_at))[0];
    const from = versionOfScan(versions, earlier?.assessment_id);
    return from && earlier && from.n !== to.n ? { from, to, previousScanId: earlier.assessment_id } : null;
}

/* ------------------------------------------------------------------------- *
 * One scan's questions, and the diff between two
 * ------------------------------------------------------------------------- */

/** One scan's questions and, when it carries them, whether each named the business. */
export interface ScanQuestions {
    assessmentId: string;
    capturedAt: string;
    queries: AeoQuery[];
    /** By question key. Null when the scan has no per-question verdicts. */
    named: Record<string, boolean> | null;
}

/** Trimmed lowercase text: the join key the backend uses. */
export const questionKey = (text: string | null | undefined) => (text ?? "").trim().toLowerCase();

const slotOf = (q: AeoQuery) => q.n ?? q.slot_n ?? null;
const normalFill = (fill: string | null | undefined) => (fill ?? "").trim().toLowerCase();
/** The same fill, counting "no fill" as one value: an intent slot carries none. */
const sameFill = (a: AeoQuery, b: AeoQuery) => normalFill(a.fill) === normalFill(b.fill);

export interface QuestionSetDiff {
    /** Asked in both sets, word for word. */
    kept: AeoQuery[];
    added: AeoQuery[];
    removed: AeoQuery[];
    /** Same slot and fill, new words. */
    reworded: { now: AeoQuery; was: AeoQuery }[];
}

export function diffQuestionSets(before: AeoQuery[], after: AeoQuery[]): QuestionSetDiff {
    const beforeKeys = new Set(before.map((q) => questionKey(q.text)));
    const afterKeys = new Set(after.map((q) => questionKey(q.text)));
    const left = before.filter((q) => !afterKeys.has(questionKey(q.text)));
    const fresh = after.filter((q) => !beforeKeys.has(questionKey(q.text)));
    const used = new Set<AeoQuery>();
    const reworded: QuestionSetDiff["reworded"] = [];
    fresh.forEach((q) => {
        const slot = slotOf(q);
        if (slot === null) return;
        const was = left.find((b) => !used.has(b) && slotOf(b) === slot && sameFill(b, q));
        if (was) {
            used.add(was);
            reworded.push({ now: q, was });
        }
    });
    const rewordedNow = new Set(reworded.map((r) => r.now));
    return {
        kept: after.filter((q) => beforeKeys.has(questionKey(q.text))),
        added: fresh.filter((q) => !rewordedNow.has(q)),
        removed: left.filter((q) => !used.has(q)),
        reworded,
    };
}

/** How many of these questions the scan named, or null when it carries no verdicts. */
export function namedCount(queries: AeoQuery[], scan: ScanQuestions | null): number | null {
    if (!scan?.named) return null;
    const named = scan.named;
    return queries.filter((q) => named[questionKey(q.text)] === true).length;
}

/**
 * The jump between two scans on different sets, split in two: the change on
 * the questions both asked (real movement), and the rest, which is which
 * questions were asked. Both halves are null when fewer than MIN_SHARED
 * questions are shared: without a measurable `real`, the rest is unknown too,
 * and the swap must not be credited with the whole jump.
 */
export interface SetJump {
    before: number;
    after: number;
    keptBefore: number;
    keptAfter: number;
    real: number | null;
    fromQuestions: number | null;
    /** Both scans fall on one day in the reader's time zone, the way the dates are shown. */
    sameDay: boolean;
}

export function setJump(diff: QuestionSetDiff, a: ScanQuestions | null, b: ScanQuestions | null): SetJump | null {
    if (!a?.named || !b?.named) return null;
    const before = namedCount(a.queries, a);
    const after = namedCount(b.queries, b);
    const keptBefore = namedCount(diff.kept, a);
    const keptAfter = namedCount(diff.kept, b);
    if (before === null || after === null || keptBefore === null || keptAfter === null) return null;
    const comparable = diff.kept.length >= MIN_SHARED;
    const real = comparable ? keptAfter - keptBefore : null;
    return {
        before,
        after,
        keptBefore,
        keptAfter,
        real,
        fromQuestions: real === null ? null : after - before - real,
        sameDay: sameLocalDay(a.capturedAt, b.capturedAt),
    };
}
